import { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import classNames from "classnames";

// Hooks
import useTranslate from "hooks/useTranslate";

import "./styles.sass";

type TimerProps = {
  isPromo?: boolean;
  disabled?: boolean;
  finishDate: string;
  defaultValue?: string;
  mask?:
    | "DD:HH:mm:ss"
    | "DD:HH:mm"
    | "DD HH mm ss"
    | "DD HH mm"
    | "HH:mm:ss"
    | "mm:ss"
    | "ss";
  isBonus?: boolean;
  isWheel?: boolean;
  warningPaint?: boolean;
  size?: "small" | "medium" | "large";
};

const INITIAL_STATE = "00:00:00";

dayjs.extend(duration);
dayjs.extend(relativeTime, {
  thresholds: [
    { l: "s", r: 1 },
    { l: "m", r: 1 },
    { l: "mm", r: 59, d: "minute" },
    { l: "h", r: 1 },
    { l: "hh", r: 23, d: "hour" },
    { l: "d", r: 1 },
    { l: "dd", r: 29 },
    { l: "M", r: 1 },
    { l: "MM", r: 11, d: "month" },
    { l: "y", r: 1 },
    { l: "yy", d: "year" },
  ],
});

const Timer = (props: TimerProps) => {
  const {
    isPromo,
    finishDate,
    defaultValue,
    disabled,
    mask = "HH:mm:ss",
    isBonus,
    isWheel,
    warningPaint,
    size = "small",
  } = props;
  const [timer, setTimer] = useState<string>(defaultValue || INITIAL_STATE);
  const t = useTranslate();
  const TimerClass = classNames("Timer", {
    // eslint-disable-next-line camelcase
    Timer_tournament: mask === "DD:HH:mm:ss",
    "Timer-bonus": isBonus || isWheel,
    wheel: isWheel,
    disabled,
  });
  const TimerBonusItemClass = classNames("Timer-bonus-item", {
    wheel: isWheel,
  });
  const TimerBonusClass = classNames("Timer-bonus", {
    wheel: isWheel,
  });

  const isLessThan72Hours = useMemo(() => {
    return (
      dayjs(finishDate).diff(dayjs()) < 72 * 60 * 60 * 1000 && warningPaint
    );
  }, [finishDate, warningPaint]);

  const maskBonus = useMemo(
    () =>
      // eslint-disable-next-line sonarjs/no-nested-template-literals
      `${mask === "DD:HH:mm:ss" || mask === "DD:HH:mm" ? `${t("tournament.card.timer.days")}:` : ""}${t(
        "tournament.card.timer.hours",
      )}:${t("tournament.card.timer.minutes")}:${mask === "DD:HH:mm" ? "" : t("tournament.card.timer.seconds")}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const maskBonusSplit = useMemo(() => maskBonus.split(":"), [maskBonus]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (finishDate) {
      const finish = dayjs(finishDate);
      const interval: NodeJS.Timeout = setInterval(() => {
        const now = dayjs();
        if (finish.diff(now, "hours") >= 24 && mask === "HH:mm:ss") {
          const diffH = finish.diff(now, "hours");
          const diffOther = dayjs.duration(finish.diff(now)).format("mm:ss");
          const diff = `${diffH}:${diffOther}`;
          if (now.isAfter(finish)) {
            setTimer(INITIAL_STATE);
            clearInterval(interval);
            return;
          }
          setTimer(diff);
        } else {
          const diff = dayjs.duration(finish.diff(now)).format(mask);
          if (now.isAfter(finish)) {
            setTimer(INITIAL_STATE);
            clearInterval(interval);
            return;
          }
          setTimer(diff);
        }
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
    setTimer(defaultValue || INITIAL_STATE);
  }, [finishDate, mask, defaultValue]);

  return (
    <div className={TimerClass}>
      {isBonus || isWheel
        ? maskBonusSplit.map((element, index) => {
            const textClass = classNames("Timer-bonus-item__value", {
              "Timer-is-promo": isPromo,
              "Timer-bonus-item__value-small": size === "small",
              "Timer-bonus-item__value-medium": size === "medium",
              "Timer-bonus-item__value-large": size === "large",
              "Timer-bonus-item__value-warning": isLessThan72Hours && index < 2,
              wheel: isWheel,
            });
            const labelClass = classNames("Timer-bonus-item__label", {
              "Timer-bonus-item__label-large": size === "large",
              wheel: isWheel,
            });
            const hrClass = classNames("Timer-bonus__hr", {
              "Timer-is-promo__hr": isPromo,
              "Timer-bonus__hr-large": size === "large",
            });

            const withoutHr =
              (element === "Minutes" && isPromo) || (index === 2 && isPromo);

            if (!element) {
              return null;
            }

            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className={TimerBonusClass}>
                <div className={TimerBonusItemClass}>
                  <div className={textClass}>{timer.split(":")[index]}</div>
                  <div className={labelClass}>{element}</div>
                </div>

                {index !== maskBonusSplit.length - 1 && !withoutHr && (
                  <hr className={hrClass} />
                )}
              </div>
            );
          })
        : timer}
    </div>
  );
};

export default Timer;
